import type { ShipSpaceId } from "../board";
import type { CharacterId, SkillType } from "../types";
import { kCharacterZh } from "./glossary";

export interface CharacterDef {
  id: CharacterId;
  /** Chinese display name (see glossary.ts). */
  name: string;
  /** Original English names. */
  en?: { name: string; ability: string; reveal: string; feat: string };
  title: string;
  skills: Partial<Record<SkillType, number>>;
  startSpace: ShipSpaceId;
  startItem: string;
  ability: { name: string; text: string };
  reveal: { name: string; text: string };
  feat: { name: string; text: string };
}

/** Swap in the Chinese names, keeping the English originals under `en`. */
const C = (d: CharacterDef): CharacterDef => {
  const zh = kCharacterZh[d.id];
  return {
    ...d,
    name: zh.name,
    en: { name: d.name, ability: d.ability.name, reveal: d.reveal.name, feat: d.feat.name },
    ability: { ...d.ability, name: zh.ability },
    reveal: { ...d.reveal, name: zh.reveal },
    feat: { ...d.feat, name: zh.feat },
  };
};

export const kCharacters: Record<CharacterId, CharacterDef> = {
  arjun: C({
    id: "arjun", name: "Arjun Singh", title: "大副", skills: { influence: 2, observation: 1, strength: 2 },
    startSpace: "bridge", startItem: "sixShooter",
    ability: { name: "Deck Chief", text: "行动：移动到任意甲板，然后执行 1 个行动。每回合限一次。" },
    reveal: { name: "Direct Off Course", text: "公开时若不在禁闭室：重置航迹。" },
    feat: { name: "Self Sacrifice", text: "危机检定加牌前，若不在禁闭室：直接指定检定结果而不结算；你被击败。" },
  }),
  beatrice: C({
    id: "beatrice", name: "Beatrice Sharpe", title: "数学家", skills: { influence: 1, lore: 2, observation: 2 },
    startSpace: "chapel", startItem: "almanac",
    ability: { name: "Predictive Analytics", text: "你的神话步骤中若不在禁闭室：多抽 1 张神话卡，1 张放牌堆底，结算另一张。" },
    reveal: { name: "Divide and Conquer", text: "公开时若不在禁闭室：选 4 个不同水域，各生成 1 个深潜者。" },
    feat: { name: "Perfect Number", text: "骰子将被掷出时，若不在禁闭室：改为把骰子设为任意面。" },
  }),
  edmund: C({
    id: "edmund", name: "Edmund Mallory", title: "霉运儿", skills: { influence: 1, lore: 1, observation: 1, strength: 1, will: 1 },
    startSpace: "galley", startItem: "luckyRing",
    ability: { name: "Fortunate Son", text: "骰子掷出或重投后，若不在禁闭室：可弃 1 张手牌重投。" },
    reveal: { name: "Spread Misfortune", text: "公开时若不在禁闭室：抽神话牌堆顶牌，结算其失败效果或一个不含检定的选项（忽略图标），然后弃掉。" },
    feat: { name: "Uncanny Fortune", text: "任意玩家的神话步骤中，神话卡结算前可弃掉它；该玩家改抽另一张结算。" },
  }),
  ishmael: C({
    id: "ishmael", name: "Ishmael Marsh", title: "流亡者", skills: { lore: 2, strength: 2, will: 1 },
    startSpace: "galley", startItem: "bookOfDagon",
    ability: { name: "First Oath of Dagon", text: "行动：选一只君主并弃 1 张学识卡掷骰；骰值+牌值 ≥7 则移入深渊，否则可移到相邻水域。" },
    reveal: { name: "Swear the Second Oath", text: "公开时：激活你所在空间的每个深潜者。" },
    feat: { name: "Deep One Ancestry", text: "行动：查看另一名人类的所有忠诚卡。" },
  }),
  jamie: C({
    id: "jamie", name: "Jamie Snell", title: "纠察长", skills: { strength: 3, will: 2 },
    startSpace: "bridge", startItem: "repeatingRifle",
    ability: { name: "Quick Draw", text: "行动：执行你某个武器物品的行动，然后执行 1 个行动。每回合限一次。" },
    reveal: { name: "Rain Fire", text: "公开时若不在禁闭室：击败你所在空间的每名人类和乘客。" },
    feat: { name: "Arrest Order", text: "行动：把你所在空间的一名角色移到禁闭室。" },
  }),
  jeanne: C({
    id: "jeanne", name: "Jeanne Lafarge", title: "工程师", skills: { influence: 1, observation: 2, strength: 2 },
    startSpace: "boiler", startItem: "toolKit",
    ability: { name: "Jury Rigger", text: "任意玩家使用锅炉房推进航迹后，若不在禁闭室：可损伤船，使航迹再前进 1 格。" },
    reveal: { name: "Sabotage", text: "公开时若不在禁闭室：损伤一个你选择的未受损室内空间（禁闭室、医务室除外）。" },
    feat: { name: "Full Steam Ahead", text: "行动：若在锅炉房，-1 燃料。然后航迹前进 2 格。" },
  }),
  keilani: C({
    id: "keilani", name: "Keilani Tatupu", title: "船长", skills: { influence: 3, will: 2 },
    startSpace: "cabin", startItem: "speakingTrumpet",
    ability: { name: "Experienced", text: "检定翻牌后，若不在禁闭室：可从手牌追加 1 张技能卡到检定。" },
    reveal: { name: "Demoralize", text: "公开时若不在禁闭室：-1 理智。" },
    feat: { name: "Unconventional Leader", text: "你将在危机上做选择时，可改为任意资源 -1，两个选项都不结算。" },
  }),
  samira: C({
    id: "samira", name: "Samira Dualeh", title: "偷渡者", skills: { lore: 1, observation: 2, will: 2 },
    startSpace: "cargo", startItem: "starbuck",
    ability: { name: "Scavenger", text: "行动：若物品 ≤4 且不在禁闭室，抽 1 张物品；然后可把你的 1 件物品交给同空间另一名人类。每回合限一次。" },
    reveal: { name: "Pickpocket", text: "公开时若不在禁闭室：选你所在空间一名人类，把其 1 件物品移到你面前。" },
    feat: { name: "Well Equipped", text: "检定总值算出后，若不在禁闭室：可让总值增减你的物品数。" },
  }),
  svetlana: C({
    id: "svetlana", name: "Svetlana Gedroits", title: "船医", skills: { influence: 2, lore: 1, observation: 2 },
    startSpace: "cabin", startItem: "travelPharmacy",
    ability: { name: "Healing Words", text: "任一角色将被击败时，可弃 1 张学识卡防止其被击败。" },
    reveal: { name: "Conduct Malpractice", text: "公开时若不在禁闭室：击败 1 名乘客（供应区或任意空间）。" },
    feat: { name: "Medical Intervention", text: "任一角色或乘客将被击败时，若不在禁闭室：防止其被击败。" },
  }),
  william: C({
    id: "william", name: "William Bowleg", title: "学徒", skills: { lore: 3, will: 2 },
    startSpace: "chapel", startItem: "elderSignAmulet",
    ability: { name: "Occult Training", text: "行动：若不在禁闭室，查看法术牌堆顶 1 张并放回顶部或底部，然后执行 1 个行动。每回合限一次。" },
    reveal: { name: "Disrupt Ritual", text: "公开时若不在禁闭室：仪式轨前进或后退至多 2 格。" },
    feat: { name: "Quick Cast", text: "行动：若你是典籍守护者，结算法术牌堆顶 1 张，然后执行 1 个行动。" },
  }),
};

export const kCaptainSuccession: CharacterId[] =
  ["keilani", "arjun", "jeanne", "jamie", "svetlana", "beatrice", "edmund", "ishmael", "william", "samira"];
export const kKeeperSuccession: CharacterId[] =
  ["william", "ishmael", "beatrice", "edmund", "samira", "svetlana", "jamie", "jeanne", "arjun", "keilani"];
